import { PaymentTypes } from 'appConstants';
import { Package } from 'pages/newSubscription/models';
import { UpgradePackageInfo } from 'pages/subscriptions/models';
import { PackagePromo } from 'subscriptionOptions/types';
import { SelectOption } from 'components/languageSearchDropdown/languageSearchDropdown';

export enum SubscriptionStep {
  CHOOSE_PACKAGE,
  SCHOOL_DETAILS,
  PROFILE_DETAILS,
  PAYMENT_TYPE,
  PAYMENT_DETAILS,
  CONFIRMATION,
}

export interface ISchoolDetails {
  schoolUrn: string;
  schoolname: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  townCity: string;
  county: string;
  country: string;
  postcodeZipcode: string;
  bCheckUrn?: boolean;
  countryList: SelectOption[] | null;
  selectedCountry?: SelectOption[] | null;
  countriesId: number;
}

export interface ISchoolUser {
  firstName: string;
  lastName: string;
  emailAddress: string;
  password: string;
  confirmPassword: string;
  schoolOfficeFirstName: string;
  schoolOfficeLastName: string;
  schoolOfficeEmailAddress: string;
  bAcceptedTermsAndConditions: boolean;
}

export interface ISchoolUserFreeTrial {
  firstName: string;
  lastName: string;
  emailAddress: string;
  password: string;
  confirmPassword: string;
  bAcceptedTermsAndConditions: boolean;
}

export interface PayByCardFormValues {
  poNumber: string;
  paymentType: PaymentTypes.PAY_BY_CARD | PaymentTypes.PAY_BY_INVOICE;
  package: UpgradePackageInfo | null;
  cardholderName: string;
  card: string;
  validPromoCode: string;
  total: number;
  validDiscount: number;
}

export class SubscribeParser {
  public static createEmptySchoolUser = (): ISchoolUser => ({
    firstName: '',
    lastName: '',
    emailAddress: '',
    password: '',
    confirmPassword: '',
    schoolOfficeFirstName: '',
    schoolOfficeLastName: '',
    schoolOfficeEmailAddress: '',
    bAcceptedTermsAndConditions: false
  });

  public static createEmptySchoolUserFreeTrial = (): ISchoolUserFreeTrial => ({
    firstName: '',
    lastName: '',
    emailAddress: '',
    password: '',
    confirmPassword: '',
    bAcceptedTermsAndConditions: false
  });  

  public static buildSchoolDetailsPayload = (values: ISchoolDetails) => ({
    ...values,
    addressLine2: values.addressLine2 ? values.addressLine2 : null,
    addressLine3: values.addressLine3 ? values.addressLine3 : null,
    county: values.county ? values.county : null,
  });

  public static buildSchoolUserDetailsPayload = (
    values: ISchoolUser,
    schoolDetails: ISchoolDetails
  ) => ({
    ...values,
    ...schoolDetails,
    addressLine2: schoolDetails.addressLine2
      ? schoolDetails.addressLine2
      : null,
    addressLine3: schoolDetails.addressLine3
      ? schoolDetails.addressLine3
      : null,
    county: schoolDetails.county ? schoolDetails.county : null,
  });

  public static buildSchoolUserDetailsPayloadFreeTrial = (
    values: ISchoolUserFreeTrial,
    schoolDetails: ISchoolDetails
  ) => ({
    ...values,
    ...schoolDetails,
    addressLine2: schoolDetails.addressLine2
      ? schoolDetails.addressLine2
      : null,
    addressLine3: schoolDetails.addressLine3
      ? schoolDetails.addressLine3
      : null,
    county: schoolDetails.county ? schoolDetails.county : null,
  });  

  public static buildArchiveNowPayload = (
    quotesId: number | null,
    registrationToken: string,
    packagePromos: PackagePromo[],
    expectedGrossAmount: number,
    poNumber: string,
    FinanceEmail: string,
  ) => {
    return {
      quotesId,
      registrationToken,
      expectedGrossAmount,
      packagePromos,
      poNumber,
      FinanceEmail,
    };
  };

  public static getPageHeaderInfo = (step: any, selectedPackageDetails: any) => {
    switch (step) {
      case SubscriptionStep.CHOOSE_PACKAGE:
        return { title: selectedPackageDetails ? 'Choose your subscription length' : 'Choose your package', nextStep: 'School details' };

      case SubscriptionStep.SCHOOL_DETAILS:
        return { title: 'Enter school details', nextStep: 'Enter two contact details' };

      case SubscriptionStep.PROFILE_DETAILS:
        return { title: 'Enter two contact details', nextStep: 'Payment type' };

      case SubscriptionStep.PAYMENT_TYPE:
        return {
          title: 'Payment type',
          nextStep: 'Payment details / Confirmation',
        };

      case SubscriptionStep.PAYMENT_DETAILS:
        return { title: 'Payment details', nextStep: '' };

      case SubscriptionStep.CONFIRMATION:
        return { title: 'Confirmation', nextStep: '' };

      default:
        return { title: '', nextStep: '' };
    }
  };

  public static parseError = (errors: any) => {
    let error: string;
    switch (errors[0].messageCode) {
      case 'Invalid_PromoCode_Expired':
        error = 'Promo code is expired';
        break;
      case 'Exisiting_SChoolName_PostcodeZipcode_Combination':
        error =
          'Hey! Your school already exists, contact admin@getset4education so we can get you started.';
        break;
      case 'Missing_PromoCode':
        error = 'Please provide a promo code';
        break;
      case `Invalid_PromoCode`:
        error = `Invalid promo code `;
        break;
      case 'EmailAddress_Already_Exists':
        error = 'Email address already exists';
        break;
      case 'OfficeEmailAddress_Already_Exists':
        error = 'Office email address already exists';
        break;        
      case 'Access_Token_Invalid':
        error = 'Sorry, something went wrong. Please try again. ';
        break;
      case 'ExpectedGrossAmount':
        error = 'Sorry, something went wrong. Please try again. ';
        break;
      case 'payment_amount_mismatch':
        error = 'Prices have changed. Please review your order and try again.';
        break;
      case 'pkey_general_payment_card_error':
        error = 'Payment failed. Please try again.';
        break;
      case 'pkey_general_payment_error':
        error = 'Payment failed. Please try again.';
        break;
      case 'pkey_general_payment_subscription_failed':
        error = 'Payment failed. Please try again.';
        break;
      case 'pkey_general_payment_failed':
        error = 'Payment failed. Please try again.';
        break;
      case 'Invalid_PromoCode_NotStarted':
        error = 'Invalid Promo code';
        break;
      case 'Invalid_SchoolUrn':
        error = 'Invalid school URN. Please check both the school URN and postcode are correct.';
        break;   
      case 'Invalid_Urn':
        error = 'Invalid school URN. Please check both the school URN and postcode are correct.';
        break;  
      case 'Exisiting_SchoolName_PostcodeZipcode_Combination':
        error = 'This school already has an account.';
        break;
      case 'Invalid Vat Number':
        error = 'Invalid Vat Number.';
        break;
      case 'Invalid_CountriesId':
        error = 'This VAT number does not apply to the country selected.';
        break;
      default:
        error = 'Sorry, something went wrong. Please try again. ';
    }
    return error;
  };
}
